import type { DateTime } from 'luxon';
import type { TimelineItemPositioningProps } from './Timeline.types';

/**
 * Calculate Positioning for `Timeline.Lane` items like `Event` and `Memory`
 */
export const timelineItemPositioning = (props: TimelineItemPositioningProps) => {
  const { orientation, offsetPx, sizePx } = props;

  const isRelative = offsetPx === undefined;

  return {
    position: !isRelative ? ('absolute' as const) : undefined,
    top: orientation === 'vertical' ? offsetPx : 0,
    right: orientation === 'vertical' ? 0 : undefined,
    bottom: orientation === 'horizontal' ? 0 : undefined,
    left: orientation === 'horizontal' ? offsetPx : 0,
    height: orientation === 'vertical' ? sizePx : undefined,
    width: orientation === 'horizontal' ? sizePx : undefined,
  };
};

type Params = {
  position: {
    top: number;
    left: number;
    height: number;
    width: number;
  };
  orientation: 'vertical' | 'horizontal';
  pxPerMinute: number;
  fromDateTime: DateTime;
  snapToMinutes: number;
};

/**
 * Converts the selection in `Timeline.Selectable` to a `started_at` and `ended_at` DateTime
 * via the `Timeline.fromDateTime`, `orientation`, and `pxPerMinute` props.
 */
export const convertSelectionToDateTime = ({
  position,
  orientation,
  pxPerMinute,
  fromDateTime,
  snapToMinutes,
}: Params) => {
  const { startPx, lengthPx } =
    orientation === 'horizontal'
      ? { startPx: position.left, lengthPx: position.width }
      : { startPx: position.top, lengthPx: position.height };

  function snap(px: number) {
    return Math.round(px / pxPerMinute / snapToMinutes) * snapToMinutes * pxPerMinute;
  }

  const startSnapped = snap(startPx);
  const lengthAdjustment = startSnapped - startPx;
  const lengthSnapped = snap(lengthPx - lengthAdjustment);

  const startMinutes = startSnapped / pxPerMinute;
  const durationMinutes = lengthSnapped / pxPerMinute;

  // Calculate start and end times
  const started_at = fromDateTime.plus({ minutes: startMinutes });
  const ended_at = started_at.plus({ minutes: durationMinutes });

  return { started_at, ended_at };
};
